import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";
import moment from "moment";
import { Button } from "@material-tailwind/react";
import { FiPrinter, FiArrowLeft } from "react-icons/fi";
import { TbFileInvoice } from "react-icons/tb";

import { getOrderDetailsAPI } from "../../../../service/api/order.api";
import { SET_FORMAT } from "../../../../config";
import { useSelector } from "react-redux";
import { handleAddToken } from "../../../../service/request";
import Logo from "../../../../assets/images/logo-footer-black.png";

const InvoiceDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const userDetails = useSelector((state) => state?.user);

  const [loading, setLoading] = useState(true);
  const [orderDetails, setOrderDetails] = useState(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        setLoading(true);
        await handleAddToken(userDetails?.token);
        const res = await getOrderDetailsAPI(id);
        if (res?.status && res?.data) {
          setOrderDetails(res.data);
        }
      } catch (err) {
        console.error("Error fetching order invoice:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchOrder();
    }
  }, [id, userDetails?.token]);

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <section className="p-10 min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-base font-proxima-semibold text-black">
            Loading Invoice...
          </p>
        </div>
      </section>
    );
  }

  if (!orderDetails) {
    return (
      <section className="p-10 min-h-screen flex flex-col items-center justify-center gap-4">
        <p className="text-lg font-proxima-semibold text-black">
          Order invoice not found.
        </p>
        <Button
          onClick={() => navigate("/orders")}
          className="shadow-none bg-black text-white px-5 py-2.5 rounded-md normal-case"
        >
          Back to Orders
        </Button>
      </section>
    );
  }

  const shipping = orderDetails?.shippingDetails || {};
  const billing = orderDetails?.billingDetails || {};
  const personal = orderDetails?.personalDetails || {};
  const items = orderDetails?.order || [];

  return (
    <section className="p-4 xl:p-10 lg:p-10 min-h-screen">
      <Helmet>
        <title>Invoice #{orderDetails?.orderId} | CIDA Perfumes</title>
      </Helmet>

      {/* TOP ACTIONS (HIDDEN IN PRINT) */}
      <div className="container-full-width mb-6 print:hidden">
        <div className="flex items-center justify-between">
          <Button
            onClick={() => navigate(`/order/${id}`)}
            className="flex items-center gap-2 bg-white hover:bg-black/5 text-black border border-authBorder px-4 py-2.5 rounded-md shadow-none hover:shadow-none normal-case font-proxima-semibold text-sm"
          >
            <FiArrowLeft className="w-4 h-4" /> Back to Order
          </Button>
          <Button
            onClick={handlePrint}
            className="flex items-center gap-2 bg-black hover:bg-neutral-800 text-white px-5 py-2.5 rounded-md shadow-none hover:shadow-none normal-case font-proxima-semibold text-sm"
          >
            <FiPrinter className="w-4 h-4" /> Print Invoice
          </Button>
        </div>
      </div>

      {/* INVOICE CARD */}
      <div className="container-full-width bg-white rounded-xl border border-authBorder p-8 xl:p-12 shadow-sm max-w-5xl mx-auto print:border-none print:shadow-none print:p-0">
        {/* INVOICE HEADER */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-authBorder pb-8 gap-4">
          <div>
            <img src={Logo} alt="CIDA Perfumes" className="w-28 h-auto object-contain" />
            <p className="text-xs font-proxima-regular text-greyText mt-2">
              Artisanal Fragrance House
            </p>
          </div>
          <div className="sm:text-right">
            <h1 className="text-2xl font-proxima-bold text-black tracking-tight flex items-center sm:justify-end gap-2">
              <TbFileInvoice className="w-6 h-6 text-black" /> INVOICE
            </h1>
            <p className="text-sm font-proxima-semibold text-black mt-1">
              Order ID: #{orderDetails?.orderId}
            </p>
            <p className="text-xs font-proxima-regular text-greyText mt-0.5">
              Date: {moment(orderDetails?.createdAt).format("DD MMMM YYYY, hh:mm A")}
            </p>
            <div className="mt-2">
              <span className="inline-block px-3 py-1 rounded text-xs font-proxima-semibold capitalize bg-green-100 text-green-800">
                Payment: {orderDetails?.paymentStatus || "Completed"}
              </span>
            </div>
          </div>
        </div>

        {/* CUSTOMER & ADDRESS DETAILS */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 py-8 border-b border-authBorder">
          <div>
            <h3 className="text-xs font-proxima-bold uppercase tracking-wider text-greyText mb-2">
              Billed To
            </h3>
            <p className="text-base font-proxima-semibold text-black">
              {personal?.firstName} {personal?.lastName}
            </p>
            <p className="text-sm font-proxima-regular text-greyText mt-1">
              {personal?.email}
            </p>
            <p className="text-sm font-proxima-regular text-greyText">
              {personal?.phoneNumber}
            </p>
            <p className="text-sm font-proxima-regular text-greyText mt-2">
              {billing?.addressLine1 || billing?.address || shipping?.addressLine1 || shipping?.address}
              {billing?.addressLine2 ? `, ${billing.addressLine2}` : ""}
            </p>
            <p className="text-sm font-proxima-regular text-greyText">
              {[billing?.city || shipping?.city, billing?.state || shipping?.state, billing?.zipCode || shipping?.zipCode]
                .filter(Boolean)
                .join(", ")}
            </p>
            <p className="text-sm font-proxima-regular text-greyText">
              {billing?.country || shipping?.country || "India"}
            </p>
          </div>

          <div>
            <h3 className="text-xs font-proxima-bold uppercase tracking-wider text-greyText mb-2">
              Shipped To
            </h3>
            <p className="text-base font-proxima-semibold text-black">
              {personal?.firstName} {personal?.lastName}
            </p>
            <p className="text-sm font-proxima-regular text-greyText mt-1">
              {personal?.email}
            </p>
            <p className="text-sm font-proxima-regular text-greyText">
              {personal?.phoneNumber}
            </p>
            <p className="text-sm font-proxima-regular text-greyText mt-2">
              {shipping?.addressLine1 || shipping?.address}
              {shipping?.addressLine2 ? `, ${shipping.addressLine2}` : ""}
            </p>
            <p className="text-sm font-proxima-regular text-greyText">
              {[shipping?.city, shipping?.state, shipping?.zipCode]
                .filter(Boolean)
                .join(", ")}
            </p>
            <p className="text-sm font-proxima-regular text-greyText">
              {shipping?.country || "India"}
            </p>
          </div>
        </div>

        {/* ITEMS TABLE */}
        <div className="py-6 border-b border-authBorder overflow-x-auto">
          <table className="w-full text-left">
            <thead>
              <tr className="border-b border-authBorder text-xs uppercase font-proxima-bold text-greyText tracking-wider">
                <th className="pb-3 w-12">#</th>
                <th className="pb-3">Product Name</th>
                <th className="pb-3">HSN Code</th>
                <th className="pb-3">Size / SKU</th>
                <th className="pb-3 text-right">Price</th>
                <th className="pb-3 text-center">Qty</th>
                <th className="pb-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-authBorder text-sm">
              {items.map((item, index) => {
                const itemPrice = item?.amount || item?.price || 0;
                const itemQty = item?.quantity || 1;
                const itemName = item?.name || item?.product?.name || "";
                const isCandle =
                  (itemName && itemName.toLowerCase().includes("candle")) ||
                  item?.type === "candles" ||
                  item?.product?.type === "candles";
                const hsnCode =
                  item?.hsn ||
                  item?.hsnCode ||
                  item?.product?.hsn ||
                  item?.product?.hsnCode ||
                  (isCandle ? "340600" : "330300");
                return (
                  <tr key={item?._id || index} className="py-3">
                    <td className="py-4 font-proxima-semibold text-greyText">
                      {index + 1}
                    </td>
                    <td className="py-4 font-proxima-semibold text-black">
                      {item?.name || item?.product?.name || "CIDA Fragrance"}
                    </td>
                    <td className="py-4 font-mono text-xs font-proxima-regular text-greyText">
                      {hsnCode}
                    </td>
                    <td className="py-4 font-proxima-regular text-greyText">
                      {item?.size || item?.product?.sku || "Standard"}
                    </td>
                    <td className="py-4 font-proxima-regular text-greyText text-right">
                      {SET_FORMAT(itemPrice)}
                    </td>
                    <td className="py-4 font-proxima-regular text-greyText text-center">
                      {itemQty}
                    </td>
                    <td className="py-4 font-proxima-bold text-black text-right">
                      {SET_FORMAT(itemPrice * itemQty)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* TOTALS SUMMARY */}
        <div className="py-6 flex flex-col sm:flex-row justify-between items-start gap-6 border-b border-authBorder">
          <div className="text-sm text-greyText">
            <p className="font-proxima-semibold text-black mb-1">
              Payment Information:
            </p>
            <p>Payment Mode: {orderDetails?.paymentMode || "Online (Razorpay)"}</p>
            {orderDetails?.paymentId && <p>Transaction ID: {orderDetails?.paymentId}</p>}
          </div>

          <div className="w-full sm:w-72 space-y-2 text-sm">
            <div className="flex justify-between font-proxima-regular text-greyText">
              <span>Subtotal:</span>
              <span className="font-proxima-semibold text-black">
                {SET_FORMAT(orderDetails?.subTotal || orderDetails?.amount || 0)}
              </span>
            </div>
            {orderDetails?.discountAmount > 0 && (
              <div className="flex justify-between font-proxima-regular text-green-700">
                <span>Discount{orderDetails?.couponCode ? ` (${orderDetails.couponCode})` : ""}:</span>
                <span>-{SET_FORMAT(orderDetails?.discountAmount)}</span>
              </div>
            )}
            <div className="flex justify-between font-proxima-regular text-greyText">
              <span>Shipping Fee:</span>
              <span className="font-proxima-semibold text-black">
                {orderDetails?.shippingCharge || orderDetails?.shippingAmount
                  ? SET_FORMAT(orderDetails?.shippingCharge || orderDetails?.shippingAmount)
                  : "Free"}
              </span>
            </div>
            <div className="border-t border-authBorder pt-2 flex justify-between font-proxima-bold text-base text-black">
              <span>Grand Total:</span>
              <span>{SET_FORMAT(orderDetails?.payAmount || orderDetails?.amount || 0)}</span>
            </div>
          </div>
        </div>

        {/* INVOICE FOOTER */}
        <div className="pt-6 text-center text-xs font-proxima-regular text-greyText">
          <p>Thank you for your business with CIDA Perfumes.</p>
          <p className="mt-1">For support or inquiries, please visit https://www.thecida.com or contact our concierge.</p>
        </div>
      </div>
    </section>
  );
};

export default InvoiceDetails;
